"use client";

import Image, { StaticImageData } from "next/image";
import * as images from "@/components/images/Images";
// import { Button } from "@/components/ui/button";
// import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Typography } from "@/components/ui/typography";
import Icon from "@/components/ui/Icon";
import { ROUTES } from "@/constants";

type AdvantagesCardProps = {
  title: string;
  imageUrl: string | StaticImageData;
  imageAlt?: string;
  description: string;
  linkHref?: string;
  linkLabel?: string;
  reverse?: boolean;
} & React.ComponentProps<"div">;

const AdvantagesCard = ({
  title,
  imageUrl,
  imageAlt,
  description,
  className,
  linkHref = "#",
  linkLabel,
  reverse,
}: AdvantagesCardProps) => {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-6 md:flex md:gap-[min(4rem,4.44vw)]",
        { "md:flex-row-reverse": reverse },
        className
      )}
    >
      <div className="inline-flex overflow-hidden rounded-2xl w-full md:min-w-[360] max-w-[495]">
        <Image
          alt={imageAlt ?? title}
          src={imageUrl}
          width={990}
          height={792}
          sizes="(max-width: 479px) 364px, 990px"
          className="w-full"
          loading="lazy"
        />
      </div>
      <div className="md:flex md:items-center">
        <div>
          <div className="grid grid-cols-1 gap-4 md:gap-6">
            <Typography variant="titleLg" asChild>
              <h2>{title}</h2>
            </Typography>
            <Typography
              variant="subHeadingSm"
              className="text-gray-600 leading-[165%] md:leading-[150%]"
              asChild
            >
              <p>{description}</p>
            </Typography>
          </div>
          {!!linkLabel && (
            <div className="mt-6 flex">
              <Link
                href={linkHref}
                className="inline-flex leading-[150%] font-semibold gap-2 text-blue md:font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                aria-label={`${linkLabel} about ${title}`}
                title={`${linkLabel} about ${title}`}
              >
                <span>{linkLabel}</span>
                <Icon
                  id="icon-arrow-right_24"
                  width={24}
                  height={24}
                  className="shrink-0 w-6 h-6"
                />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function AdvantagesBlock() {
  return (
    <section className="section">
      <div className="container mx-auto grid grid-cols-1 pb-12 md:pb-0">
        {/* Block 1 */}
        <AdvantagesCard
          className="py-6 sm:py-10 lg:py-20"
          imageUrl={images.advantage1}
          title="Elite network of translation professionals"
          description="Our platform intelligently matches your project to an expert with specialized experience in your industry, use case, and deliverable for context-aware precision in 100+ languages."
        />

        {/* Block 2 */}
        <AdvantagesCard
          className="py-6 sm:py-10 lg:py-20"
          imageUrl={images.advantage2}
          title="Fast high-quality online translations"
          description="Achieve unmatched accuracy with a rigorous multi-human translation and review workflow with built-in quality assurance and unlimited free revisions."
          linkHref={"/quality-guarantee"}
          linkLabel="Explore quality guarantee"
          reverse
        />

        {/* Block 3 */}
        <AdvantagesCard
          className="py-6 sm:py-10 lg:py-20"
          imageUrl={images.advantage3}
          title="Secure, user-friendly translation services platform"
          description="Get to market faster with a friction-free platform that
              centralizes and streamlines order and delivery of any project
              complexity or volume."
          linkHref={ROUTES.documentsAndDeliverables}
          linkLabel="Learn more"
        />
      </div>
    </section>
  );
}
